/**
 * Account Schema Definitions
 *
 * Spec-first schema definitions for account/org/workspace hierarchy.
 * These schemas drive oRPC type generation and validation.
 */

import { z } from "zod";
import {
  UuidField,
  TimestampField,
  MetadataField,
  ErrorResponse,
} from "./common";

// Core Account entity (enterprise tenant)
const Account = z.object({
  id: UuidField,
  name: z.string().min(1).max(200),
  email: z.email().optional(),
  metadata: MetadataField,
  createdAt: TimestampField,
  updatedAt: TimestampField,
});

/** Organization slug: globally unique, human-readable, immutable (R70 Q15a, Q16a). */
const ORGANIZATION_SLUG_MAX_LENGTH = 100;
const OrganizationSlugField = z.string().max(ORGANIZATION_SLUG_MAX_LENGTH).regex(/^[a-z0-9]+(-[a-z0-9]+)*$/);

/** Key suffix reserved for an organization's public workspace: `<org slug>-public` (R70 Q15a, Q19a). */
export const PUBLIC_WORKSPACE_KEY_SUFFIX = "-public";
export const publicWorkspaceKey = (organizationSlug: string): string => `${organizationSlug}${PUBLIC_WORKSPACE_KEY_SUFFIX}`;
const WorkspaceKeyField = z.string().min(1).max(255);
/** A key other workspaces may carry: never the reserved public suffix (R70 Q19a). */
const NonPublicWorkspaceKeyField = WorkspaceKeyField.refine((key) => !key.endsWith(PUBLIC_WORKSPACE_KEY_SUFFIX), {
  message: `key must not end with "${PUBLIC_WORKSPACE_KEY_SUFFIX}" (reserved for the public workspace)`,
});

// Core Organization entity
const Organization = z.object({
  id: UuidField,
  accountId: UuidField,
  slug: OrganizationSlugField,
  name: z.string().min(1).max(200),
  metadata: MetadataField,
  createdAt: TimestampField,
  updatedAt: TimestampField,
});

// Core Workspace entity
const Workspace = z.object({
  id: UuidField,
  organizationId: UuidField,
  key: WorkspaceKeyField.optional(),
  name: z.string().min(1).max(200),
  metadata: MetadataField,
  createdAt: TimestampField,
  updatedAt: TimestampField,
});

// Request schemas for Account
const CreateAccountRequest = z.object({
  name: z.string().min(1).max(200),
  email: z.email().optional(),
  metadata: MetadataField,
});

const UpdateAccountRequest = z.object({
  name: z.string().min(1).max(200).optional(),
  email: z.email().optional(),
  metadata: MetadataField,
});

// Request schemas for Organization
const CreateOrganizationRequest = z.object({
  accountId: UuidField,
  slug: OrganizationSlugField,
  name: z.string().min(1).max(200),
  metadata: MetadataField,
});

// No slug: it is immutable (R70 Q18a); the API route rejects a request naming it.
const UpdateOrganizationRequest = z.object({
  accountId: UuidField.optional(), // Allow moving org to different account
  name: z.string().min(1).max(200).optional(),
  metadata: MetadataField,
});

// Request schemas for Workspace
const CreateWorkspaceRequest = z.object({
  organizationId: UuidField,
  key: NonPublicWorkspaceKeyField.optional(),
  name: z.string().min(1).max(200),
  metadata: MetadataField,
});

const UpdateWorkspaceRequest = z.object({
  organizationId: UuidField.optional(), // Allow moving workspace to different org
  key: NonPublicWorkspaceKeyField.optional(),
  name: z.string().min(1).max(200).optional(),
  metadata: MetadataField,
});

// Hierarchy response
const WorkspaceHierarchy = z.object({
  workspaceId: UuidField,
  workspaceName: z.string(),
  organizationId: UuidField,
  organizationName: z.string(),
  accountId: UuidField,
  accountName: z.string(),
});

// Event schemas
const AccountCreateEvent = z.object({
  action: z.literal("created"),
  account: Account,
  timestamp: TimestampField,
});

const AccountUpdateEvent = z.object({
  action: z.literal("updated"),
  account: Account,
  timestamp: TimestampField,
});

const AccountDeleteEvent = z.object({
  action: z.literal("deleted"),
  accountId: UuidField,
  timestamp: TimestampField,
});

const OrganizationCreateEvent = z.object({
  action: z.literal("created"),
  organization: Organization,
  timestamp: TimestampField,
});

const OrganizationUpdateEvent = z.object({
  action: z.literal("updated"),
  organization: Organization,
  timestamp: TimestampField,
});

const OrganizationDeleteEvent = z.object({
  action: z.literal("deleted"),
  organizationId: UuidField,
  accountId: UuidField,
  timestamp: TimestampField,
});

const WorkspaceCreateEvent = z.object({
  action: z.literal("created"),
  workspace: Workspace,
  timestamp: TimestampField,
});

const WorkspaceUpdateEvent = z.object({
  action: z.literal("updated"),
  workspace: Workspace,
  timestamp: TimestampField,
});

const WorkspaceDeleteEvent = z.object({
  action: z.literal("deleted"),
  workspaceId: UuidField,
  organizationId: UuidField,
  timestamp: TimestampField,
});

// Internal authorization store row: (principal sub, principal kind, scope, scopeId, role).
// Platform scope is not a row (root platform admin is env-configured).
const MembershipScope = z.enum(["account", "workspace"]);
const MembershipRole = z.enum(["registered", "admin"]);
const MembershipPrincipalKind = z.enum(["user", "service"]);

const Membership = z.object({
  principalSub: z.string().min(1),
  principalKind: MembershipPrincipalKind,
  scope: MembershipScope,
  scopeId: UuidField,
  role: MembershipRole,
  createdAt: TimestampField,
  updatedAt: TimestampField,
});

// Export all schemas
export const schemas = {
  Account,
  Organization,
  OrganizationSlugField,
  Workspace,
  MembershipScope,
  MembershipRole,
  MembershipPrincipalKind,
  Membership,
  CreateAccountRequest,
  UpdateAccountRequest,
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
  CreateWorkspaceRequest,
  UpdateWorkspaceRequest,
  WorkspaceHierarchy,
  AccountCreateEvent,
  AccountUpdateEvent,
  AccountDeleteEvent,
  OrganizationCreateEvent,
  OrganizationUpdateEvent,
  OrganizationDeleteEvent,
  WorkspaceCreateEvent,
  WorkspaceUpdateEvent,
  WorkspaceDeleteEvent,
  ErrorResponse,
};

// oRPC Type Inference Support
export type SchemaTypes = {
  [K in keyof typeof schemas]: z.infer<(typeof schemas)[K]>;
};

export type { SchemaTypes as AccountSchemaTypes };
export { schemas as accountSchemas };

